// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import { classNames } from '../utils';

export const ELLIPSIS = '…';

export function calcPagination(currentPage = '1', totalPages = '1') {
  const current = parseInt(currentPage, 10);
  const total = parseInt(totalPages, 10);

  return [...new Array(total)]
    .map((_, index) => index + 1)
    .filter((number) => {
      if (total <= 5) return true;

      if (
        number === 1 ||
        number === current - 2 ||
        number === current - 1 ||
        number === current ||
        number === current + 1 ||
        number === current + 2 ||
        number === total
      ) {
        return true;
      }

      return false;
    })
    .flatMap((number, index, array) => {
      if (index > 0 && number - 1 !== array[index - 1]) {
        return [ELLIPSIS, number];
      }

      return number;
    })
    .map(String);
}

export function Pagination({
  className,
  currentPage = '1',
  totalPages,
  onPageChange,
  hideSummary = false
}) {
  if (parseInt(totalPages, 10) <= 1) return null;

  return (
    <div className={classNames('pagination', className)}>
      <ol className="pagination__list" role="list">
        <li>
          <button
            aria-label="Go to previous page"
            disabled={currentPage === '1'}
            onClick={() => onPageChange(`${parseInt(currentPage, 10) - 1}`)}
          ></button>
        </li>
        {calcPagination(currentPage, totalPages).map((page, index) => (
          <li key={index}>
            <button
              onClick={() => onPageChange(page)}
              disabled={page === currentPage || page === ELLIPSIS}
              aria-label={`Go to page ${page}`}
              className={classNames(
                page === currentPage && 'current',
                parseInt(page, 10) === parseInt(currentPage, 10) - 2 &&
                  parseInt(currentPage, 10) !== parseInt(totalPages, 10) &&
                  'lg-only',
                parseInt(page, 10) === parseInt(currentPage, 10) + 2 &&
                  parseInt(currentPage, 10) !== 1 &&
                  'lg-only',
                parseInt(page, 10) === 1 &&
                  parseInt(currentPage, 10) > 2 &&
                  'lg-only',
                parseInt(page, 10) === parseInt(totalPages, 10) &&
                  parseInt(currentPage, 10) + 1 < parseInt(totalPages, 10) &&
                  'lg-only',
                page === ELLIPSIS && 'lg-only'
              )}
            >
              {page}
            </button>
          </li>
        ))}
        <li>
          <button
            aria-label="Go to next page"
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(`${parseInt(currentPage, 10) + 1}`)}
          ></button>
        </li>
      </ol>
      {hideSummary ? null : (
        <span className="pagination__summary">
          Page {currentPage} of {totalPages}
        </span>
      )}
    </div>
  );
}

Pagination.propTypes = {
  className: PropTypes.string,
  currentPage: PropTypes.string,
  totalPages: PropTypes.string,
  onPageChange: PropTypes.func,
  hideSummary: PropTypes.bool
};
